import EditIcon from '@mui/icons-material/Edit';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import {
  Box,
  Dialog,
  DialogContent,
  IconButton,
  Skeleton,
  Typography,
} from '@mui/material';
import { Stack } from '@mui/material';
import { getBusinessUnits } from 'lib/api/business';
import { CreateFormPruffConnect } from 'lib/api/express/sgrWarrantyRequest/types';
import { CreateForm, UpdateForm } from 'lib/api/sgr/types';
import AddressEditor from 'lib/common/Forms/Warranty/Common/AddressEditor';
import AlertBanner from 'lib/designSystem/AlertBanner';
import InfoCard from 'lib/designSystem/InfoCard';
import PageHeader from 'lib/designSystem/PageHeader';
import { formatAddress } from 'lib/functions/utils';
import { BusinessSummary, BusinessUnit } from 'lib/types/models';
import React, { useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';

type WarrantyBusinessForm = Pick<
  CreateForm | UpdateForm | CreateFormPruffConnect,
  'principalRut' | 'unitCode'
> & {
  address?: string | null;
};

type Props<TForm extends WarrantyBusinessForm> = {
  open: boolean;
  onClose: () => void;
  form: TForm;
  setForm: (form: TForm) => void;
  canEditAddress?: boolean;
};

const BusinessUnitsDialog = <TForm extends WarrantyBusinessForm>({
  open,
  onClose,
  form,
  setForm,
  canEditAddress = true,
}: Props<TForm>) => {
  const dispatch = useDispatch();
  const { data, isPending } = getBusinessUnits(dispatch, form.principalRut);

  const business = data?.business;
  const units = data?.units ?? [];

  const [editingAddress, setEditingAddress] = useState(false);

  const businessSummary: BusinessSummary | null = useMemo(() => {
    return business?.BusinessSummary[0] ?? null;
  }, [business]);

  const getAddressLabel = (summary: BusinessSummary | null) => {
    if (!summary) return 'Sin dirección';
    const formatted = formatAddress(summary);
    return formatted === 'Sin dirección disponible'
      ? 'Sin dirección'
      : formatted;
  };

  const getUnitAddressLabel = (unit: BusinessUnit) => {
    if (!unit.address) return 'Sin dirección';
    const formatted = formatAddress(unit.address);
    return formatted === 'Sin dirección disponible'
      ? 'Sin dirección'
      : formatted;
  };

  const handleBusinessSelect = () => {
    const address = getAddressLabel(businessSummary);
    setForm({
      ...form,
      unitCode: null,
      ...(Object.prototype.hasOwnProperty.call(form, 'address')
        ? { address }
        : {}),
    } as TForm);
    onClose();
  };

  const handleUnitSelect = (unit: BusinessUnit) => {
    const address = getUnitAddressLabel(unit);
    setForm({
      ...form,
      unitCode: unit.unitCode,
      ...(Object.prototype.hasOwnProperty.call(form, 'address')
        ? { address }
        : {}),
    } as TForm);
    onClose();
  };

  if (isPending) {
    return (
      <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
        <DialogContent>
          <Box sx={{ py: 2 }}>
            <Box sx={{ mb: 2 }}>
              <Skeleton variant="rectangular" width={180} height={36} />
              <Skeleton variant="text" width={200} sx={{ mt: 1 }} />
              <Skeleton variant="text" width={120} />
            </Box>
            <Skeleton
              variant="rectangular"
              width="100%"
              height={60}
              sx={{ mb: 2, borderRadius: 1 }}
            />
            <Skeleton
              variant="rectangular"
              width="100%"
              height={40}
              sx={{ mb: 1, borderRadius: 1 }}
            />
            <Skeleton
              variant="rectangular"
              width="100%"
              height={40}
              sx={{ mb: 1, borderRadius: 1 }}
            />
          </Box>
        </DialogContent>
      </Dialog>
    );
  }

  if (!business) {
    return (
      <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
        <DialogContent>
          <PageHeader.TitleOnly
            title="Unidades de Negocio"
            iconType="search"
            subtitle="Edita la dirección de la empresa y revisa las unidades de negocio"
            variant="dialogHeader"
          />
          <AlertBanner
            severity="error"
            title="Empresa no encontrada"
            message={`No pudimos encontrar una empresa con el identificador ${form.principalRut}. Puedes ingresar la dirección manualmente.`}
          />
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogContent>
        <PageHeader.TitleOnly
          title="Unidades de Negocio"
          iconType="search"
          subtitle="Selecciona una unidad de negocio o la empresa para autocompletar la dirección"
          variant="dialogHeader"
        />
        <Stack direction="column" gap={2}>
          <Box
            onClick={() => handleBusinessSelect()}
            sx={{
              borderRadius: 2,
              border: '1px solid',
              borderColor: 'grey.200',
              p: 2,
              display: 'flex',
              flexDirection: 'column',
              gap: 1,
              transition: 'border-color 0.2s ease',
              cursor: 'pointer',
              '&:hover': {
                borderColor: 'primary.main',
              },
            }}
          >
            <Stack direction="row" justifyContent="space-between">
              <Typography variant="subtitle2" fontWeight="bold">
                {business.name}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {business.identifier}
              </Typography>
            </Stack>
            {canEditAddress && editingAddress ? (
              <AddressEditor
                summary={businessSummary}
                identifier={form.principalRut}
                onCancel={() => setEditingAddress(false)}
              />
            ) : (
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
              >
                <InfoCard
                  icon={<LocationOnIcon />}
                  label="Dirección"
                  value={getAddressLabel(businessSummary)}
                  color="orange"
                />
                {canEditAddress && (
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      setEditingAddress(true);
                    }}
                    sx={{ p: 0.5 }}
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                )}
              </Stack>
            )}
          </Box>

          {units.map((unit) => (
            <Box
              key={unit.unitCode}
              onClick={() => handleUnitSelect(unit)}
              sx={{
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'grey.200',
                p: 2,
                display: 'flex',
                flexDirection: 'column',
                gap: 1,
                transition: 'border-color 0.2s ease',
                cursor: 'pointer',
                '&:hover': {
                  borderColor: 'primary.main',
                },
              }}
            >
              <Stack direction="row" justifyContent="space-between">
                <Typography variant="subtitle2" fontWeight="bold">
                  {unit.name}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {unit.unitCode}
                </Typography>
              </Stack>

              <InfoCard
                icon={<LocationOnIcon />}
                label="Dirección"
                value={getUnitAddressLabel(unit)}
                color="orange"
              />
            </Box>
          ))}
        </Stack>
      </DialogContent>
    </Dialog>
  );
};

export default BusinessUnitsDialog;
